import type { UploadVersionResult } from '@atlas/api-contract';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, FileWarning } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { formatBytes } from '@/lib/format/bytes';

export interface UploadResultViewProps {
  result: UploadVersionResult;
  /** Closes the dialog. The link navigates away, so it closes it too. */
  onDone: () => void;
  /** Resets the dialog to a fresh file picker. */
  onUploadAnother: () => void;
}

/**
 * What actually landed, after a successful upload.
 *
 * Two things are worth a person's attention here and they are ranked
 * accordingly.
 *
 * **The dropped files come first.** A dropped file is the one way an upload can
 * succeed and still not be what the designer meant: the version exists, the
 * review can start, and a font or a video is quietly missing. The server
 * reports every skipped entry with a reason (spec R1.5, R1.10), so every one of
 * them is listed with that reason next to it — not summarised as "3 files were
 * skipped", which would leave the reader to guess whether any of them mattered.
 *
 * **Then the way in.** The point of uploading is to look at it, so the primary
 * action is a link straight into the version that was just created rather than
 * a "Close" that returns to the same card the user started from.
 */
export const UploadResultView = ({
  result,
  onDone,
  onUploadAnother,
}: UploadResultViewProps) => {
  const { version, droppedFiles } = result;
  const screenCount = version.screenPaths.length;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-md border border-border bg-muted/40 p-4">
        <CheckCircle2
          aria-hidden="true"
          strokeWidth={1.75}
          className="mt-0.5 size-4 shrink-0 text-foreground"
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Version {version.versionNumber} is ready to review
          </p>
          <p className="text-sm text-muted-foreground">
            {version.fileCount} {version.fileCount === 1 ? 'file' : 'files'} ·{' '}
            {formatBytes(version.totalBytes)} · {screenCount}{' '}
            {screenCount === 1 ? 'screen' : 'screens'}, opening at{' '}
            <span className="font-mono text-xs text-foreground">
              {version.entryPath}
            </span>
          </p>
        </div>
      </div>

      {droppedFiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Every file in the archive was imported.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <FileWarning
              aria-hidden="true"
              strokeWidth={1.75}
              className="size-4 text-muted-foreground"
            />
            <p className="text-sm font-medium text-foreground">
              {droppedFiles.length}{' '}
              {droppedFiles.length === 1 ? 'file was' : 'files were'} skipped
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            The version was created without them. If the prototype needs one of
            these, convert it to a supported format and upload again.
          </p>
          <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-md border border-border">
            {droppedFiles.map((dropped) => (
              <li key={dropped.path} className="space-y-0.5 px-3 py-2">
                <p
                  className="truncate font-mono text-xs text-foreground"
                  title={dropped.path}
                >
                  {dropped.path}
                </p>
                <p className="text-xs text-muted-foreground">{dropped.reason}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="ghost" onClick={onUploadAnother}>
          Upload another
        </Button>
        <Button type="button" variant="outline" onClick={onDone}>
          Done
        </Button>
        <Button asChild>
          <Link
            to="/prototypes/$prototypeId"
            params={{ prototypeId: version.prototypeId }}
            search={{ version: version.id }}
            onClick={onDone}
          >
            Open version {version.versionNumber}
          </Link>
        </Button>
      </div>
    </div>
  );
};
